/**
 * Product Form Page
 * =================
 * Create or edit a product.
 */

import { useState, useEffect } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { ArrowLeft, Save, ImageIcon, Search, Sparkles, Loader2, CheckCircle, AlertCircle, Plus, Minus, X, AlertTriangle, Camera } from 'lucide-react'
import { AxiosError } from 'axios'
import { Card, CardContent, CardHeader } from '@/components/Card'
import Button from '@/components/Button'
import Input from '@/components/Input'
import ImageUpload from '@/components/ImageUpload'
import ImageGallery from '@/components/ImageGallery'
import ImageSearchModal from '@/components/ImageSearchModal'
import SKULookupModal from '@/components/SKULookupModal'
import ServiceTagLookupModal from '@/components/ServiceTagLookupModal'
import BarcodeScannerModal from '@/components/BarcodeScannerModal'
import ProductPhotoCapture from '@/components/ProductPhotoCapture'
import ProductIdentificationModal from '@/components/ProductIdentificationModal'
import CascadingCategorySelect from '@/components/CascadingCategorySelect'
import { useProduct, useCreateProduct, useUpdateProduct } from '@/hooks/useProducts'
import { useCategories, useCreateCategory } from '@/hooks/useCategories'
import { useLocations, useCreateLocation } from '@/hooks/useLocations'
import { useManufacturers, useCreateManufacturer } from '@/hooks/useManufacturers'
import { useBarcodeLookup } from '@/hooks/useBarcodeLookup'
import { useQueryClient } from '@tanstack/react-query'
import { productKeys } from '@/hooks/useProducts'
import { productImageApi, compatibilitySearchApi } from '@/services/api'
import type { ProductCreate, ProductStatus } from '@/types'
import { PRODUCT_STATUS_LABELS } from '@/types'

type BarcodeLookupStatus = 'idle' | 'loading' | 'found' | 'partial' | 'not_found' | 'error'

export default function ProductForm() {
  const navigate = useNavigate()
  const { productId } = useParams<{ productId: string }>()
  const [searchParams] = useSearchParams()
  const isEditing = !!productId
  const queryClient = useQueryClient()

  // Get barcode from query params (used when coming from Dashboard barcode scan)
  const initialBarcode = searchParams.get('barcode') || ''

  // Fetch existing product if editing
  const { data: productData, isLoading: productLoading } = useProduct(productId ?? '')
  const product = productData?.data

  // Fetch categories, locations, and manufacturers for dropdowns
  const { data: categoriesData, refetch: refetchCategories } = useCategories()
  const { data: locationsData, refetch: refetchLocations } = useLocations()
  const { data: manufacturersData, refetch: refetchManufacturers } = useManufacturers()

  const createProduct = useCreateProduct()
  const updateProduct = useUpdateProduct()
  const createCategory = useCreateCategory()
  const createLocation = useCreateLocation()
  const createManufacturer = useCreateManufacturer()
  const barcodeLookup = useBarcodeLookup()

  // Barcode lookup state
  const [barcodeLookupStatus, setBarcodeLookupStatus] = useState<BarcodeLookupStatus>('idle')
  const [barcodeLookupSource, setBarcodeLookupSource] = useState<string | null>(null)

  // State for image search modal
  const [showImageSearch, setShowImageSearch] = useState(false)
  const [pendingImageUrl, setPendingImageUrl] = useState<string | null>(null)

  // State for SKU lookup modal
  const [showSKULookup, setShowSKULookup] = useState(false)

  // State for barcode scanner modal
  const [showBarcodeScanner, setShowBarcodeScanner] = useState(false)

  // State for service tag lookup modal
  const [showServiceTagLookup, setShowServiceTagLookup] = useState(false)
  const [serviceTagManufacturer, setServiceTagManufacturer] = useState<string>('auto')

  // State for product identification
  const [capturedProductImage, setCapturedProductImage] = useState<string | null>(null)
  const [isIdentifying, setIsIdentifying] = useState(false)

  // State for creating new category inline
  const [showNewCategory, setShowNewCategory] = useState(false)
  const [newCategoryName, setNewCategoryName] = useState('')
  const [newCategoryParentId, setNewCategoryParentId] = useState<number | null>(null)

  // State for creating new location inline
  const [showNewLocation, setShowNewLocation] = useState(false)
  const [newLocationName, setNewLocationName] = useState('')

  // State for creating new manufacturer inline
  const [showNewManufacturer, setShowNewManufacturer] = useState(false)
  const [newManufacturerName, setNewManufacturerName] = useState('')

  // State for form submission error
  const [submitError, setSubmitError] = useState<string | null>(null)

  // State for compatibility search
  const [isSearchingCompatibility, setIsSearchingCompatibility] = useState(false)

  // State for "Save & Add Another" mode
  const [saveAndAddAnother, setSaveAndAddAnother] = useState(false)

  // Refresh product data after image changes
  const handleImageChange = () => {
    if (product) {
      queryClient.invalidateQueries({ queryKey: productKeys.detail(product.id) })
    }
  }

  // Handle image selected from search
  const handleImageSelected = async (imageUrl: string) => {
    if (isEditing && product) {
      // Editing mode: add image directly to product
      try {
        await productImageApi.addByUrl(product.id, {
          image_url: imageUrl,
          is_primary: !product.images || product.images.length === 0,
          source: 'web_search',
          alt_text: `${product.name} - from image search`,
        })
        handleImageChange()
      } catch (error) {
        console.error('Failed to add image:', error)
      }
    } else {
      // New product mode: store URL to add after product creation
      setPendingImageUrl(imageUrl)
    }
  }

  // Get search query from product name and SKU
  const getImageSearchQuery = () => {
    const parts = []
    if (formData.name) parts.push(formData.name)
    if (formData.sku) parts.push(formData.sku)
    return parts.join(' ') || 'product'
  }

  // Find manufacturer by name (case-insensitive)
  const findManufacturerByName = (name: string) => {
    return manufacturersData?.data?.find(
      (m) => m.name.toLowerCase() === name.toLowerCase()
    )
  }

  // Handle scanned barcode from camera
  const handleBarcodeScanned = (barcode: string) => {
    setFormData((prev) => ({ ...prev, barcode }))
    setShowBarcodeScanner(false)
    // Trigger lookup automatically
    handleBarcodeLookup(barcode)
  }

  // Handle barcode lookup
  const handleBarcodeLookup = async (barcode: string) => {
    if (!barcode) return

    setBarcodeLookupStatus('loading')
    setBarcodeLookupSource(null)

    try {
      const response = await barcodeLookup.mutateAsync(barcode)
      const result = response.data

      if (result.found) {
        // Determine if we got full or partial data
        const hasAllFields = result.name && result.description && result.brand
        setBarcodeLookupStatus(hasAllFields ? 'found' : 'partial')
        setBarcodeLookupSource(result.source)

        // Auto-fill form fields
        setFormData((prev) => ({
          ...prev,
          barcode: barcode,
          name: result.name || prev.name,
          description: result.description || prev.description,
        }))

        // Set image if available
        if (result.image_url) {
          setPendingImageUrl(result.image_url)
        }

        // Handle manufacturer (create if not found)
        if (result.brand) {
          const existingManufacturer = findManufacturerByName(result.brand)
          if (existingManufacturer) {
            setFormData((prev) => ({
              ...prev,
              manufacturer_id: existingManufacturer.id,
            }))
          } else {
            // Create new manufacturer with logo if available
            try {
              const newManufacturer = await createManufacturer.mutateAsync({
                name: result.brand,
                logo_url: result.brand_logo_url || undefined,
              })
              if (newManufacturer.data) {
                await refetchManufacturers()
                setFormData((prev) => ({
                  ...prev,
                  manufacturer_id: newManufacturer.data!.id,
                }))
              }
            } catch (err) {
              console.error('Failed to create manufacturer:', err)
            }
          }
        }
      } else {
        setBarcodeLookupStatus('not_found')
        // Still set the barcode even if not found
        setFormData((prev) => ({
          ...prev,
          barcode: barcode,
        }))
      }
    } catch (error) {
      console.error('Barcode lookup failed:', error)
      setBarcodeLookupStatus('error')
      // Still set the barcode even on error
      setFormData((prev) => ({
        ...prev,
        barcode: barcode,
      }))
    }
  }

  // Get barcode lookup status display
  const getBarcodeLookupStatusDisplay = () => {
    switch (barcodeLookupStatus) {
      case 'loading':
        return {
          icon: <Loader2 className="animate-spin text-primary-500" size={16} />,
          message: 'Looking up barcode...',
          color: 'text-primary-600 dark:text-primary-400',
        }
      case 'found':
        return {
          icon: <CheckCircle className="text-green-500" size={16} />,
          message: `Found via ${barcodeLookupSource}`,
          color: 'text-green-600 dark:text-green-400',
        }
      case 'partial':
        return {
          icon: <CheckCircle className="text-yellow-500" size={16} />,
          message: `Partial match via ${barcodeLookupSource}`,
          color: 'text-yellow-600 dark:text-yellow-400',
        }
      case 'not_found':
        return {
          icon: <AlertCircle className="text-gray-400" size={16} />,
          message: 'Not found in database',
          color: 'text-gray-500 dark:text-gray-400',
        }
      case 'error':
        return {
          icon: <AlertCircle className="text-red-500" size={16} />,
          message: 'Lookup failed',
          color: 'text-red-600 dark:text-red-400',
        }
      default:
        return null
    }
  }

  // Handle applying SKU lookup results
  const handleSKULookupApply = async (data: {
    name?: string
    description?: string
    manufacturer?: string
    compatible_products?: string
    imageUrl?: string
  }) => {
    setFormData(prev => ({
      ...prev,
      ...(data.name && { name: data.name }),
      ...(data.description && { description: data.description }),
      ...(data.compatible_products && { compatible_products: data.compatible_products }),
    }))

    // Handle manufacturer - try to find existing one by name, or create if not found
    if (data.manufacturer) {
      const existingMfr = findManufacturerByName(data.manufacturer)
      if (existingMfr) {
        setFormData(prev => ({ ...prev, manufacturer_id: existingMfr.id }))
      } else {
        // Create new manufacturer (logo will be auto-generated by backend)
        try {
          const newManufacturer = await createManufacturer.mutateAsync({
            name: data.manufacturer,
          })
          if (newManufacturer.data) {
            await refetchManufacturers()
            setFormData(prev => ({
              ...prev,
              manufacturer_id: newManufacturer.data!.id,
            }))
          }
        } catch (err) {
          console.error('Failed to create manufacturer:', err)
        }
      }
    }

    // Handle image URL
    if (data.imageUrl) {
      if (isEditing && product) {
        // Add image to existing product
        productImageApi.addByUrl(product.id, {
          image_url: data.imageUrl,
          is_primary: !product.images || product.images.length === 0,
          source: 'web_search',
          alt_text: data.name || 'Product image from SKU lookup',
        }).then(() => handleImageChange())
          .catch(err => console.error('Failed to add image:', err))
      } else {
        // Store for new product
        setPendingImageUrl(data.imageUrl)
      }
    }
  }

  // Handle applying service tag lookup results
  const handleServiceTagLookupApply = async (data: {
    name?: string
    description?: string
    manufacturer?: string
  }) => {
    setFormData(prev => ({
      ...prev,
      ...(data.name && { name: data.name }),
      ...(data.description && { description: data.description }),
    }))

    // Handle manufacturer - try to find existing one by name, or create if not found
    if (data.manufacturer) {
      const existingMfr = findManufacturerByName(data.manufacturer)
      if (existingMfr) {
        setFormData(prev => ({ ...prev, manufacturer_id: existingMfr.id }))
      } else {
        // Create new manufacturer (logo will be auto-generated by backend)
        try {
          const newManufacturer = await createManufacturer.mutateAsync({
            name: data.manufacturer,
          })
          if (newManufacturer.data) {
            await refetchManufacturers()
            setFormData(prev => ({
              ...prev,
              manufacturer_id: newManufacturer.data!.id,
            }))
          }
        } catch (err) {
          console.error('Failed to create manufacturer:', err)
        }
      }
    }
  }

  // Handle photo capture for product identification
  const handleProductPhotoCapture = (imageBase64: string) => {
    setIsIdentifying(true)
    setCapturedProductImage(imageBase64)
  }

  // Handle applying product identification results
  const handleProductIdentificationApply = async (data: {
    name?: string
    description?: string
    manufacturer?: string
    category?: string
    compatible_products?: string
    imageUrl?: string
  }) => {
    setFormData(prev => ({
      ...prev,
      ...(data.name && { name: data.name }),
      ...(data.description && { description: data.description }),
      ...(data.compatible_products && { compatible_products: data.compatible_products }),
    }))

    // Handle manufacturer - try to find existing one by name, or create if not found
    if (data.manufacturer) {
      const existingMfr = findManufacturerByName(data.manufacturer)
      if (existingMfr) {
        setFormData(prev => ({ ...prev, manufacturer_id: existingMfr.id }))
      } else {
        // Create new manufacturer (logo will be auto-generated by backend)
        try {
          const newManufacturer = await createManufacturer.mutateAsync({
            name: data.manufacturer,
          })
          if (newManufacturer.data) {
            await refetchManufacturers()
            setFormData(prev => ({
              ...prev,
              manufacturer_id: newManufacturer.data!.id,
            }))
          }
        } catch (err) {
          console.error('Failed to create manufacturer:', err)
        }
      }
    }

    // Handle image URL
    if (data.imageUrl) {
      if (isEditing && product) {
        // Add image to existing product
        productImageApi.addByUrl(product.id, {
          image_url: data.imageUrl,
          is_primary: !product.images || product.images.length === 0,
          source: 'ai_identification',
          alt_text: data.name || 'Product image from AI identification',
        }).then(() => handleImageChange())
          .catch(err => console.error('Failed to add image:', err))
      } else {
        // Store for new product
        setPendingImageUrl(data.imageUrl)
      }
    }

    // Reset identification state
    setCapturedProductImage(null)
    setIsIdentifying(false)
  }

  // Handle closing identification modal
  const handleIdentificationClose = () => {
    setCapturedProductImage(null)
    setIsIdentifying(false)
  }

  // Handle compatibility search
  const handleCompatibilitySearch = async () => {
    if (!formData.name?.trim()) return

    setIsSearchingCompatibility(true)

    try {
      // Get manufacturer name if selected
      const manufacturerName = formData.manufacturer_id
        ? manufacturersData?.data?.find((m) => m.id === formData.manufacturer_id)?.name
        : undefined

      const response = await compatibilitySearchApi.search({
        name: formData.name,
        sku: formData.sku || undefined,
        manufacturer: manufacturerName,
        description: formData.description || undefined,
      })

      if (response.data && response.data.compatible_devices.length > 0) {
        // Format the compatible devices as a newline-separated list
        const devicesText = response.data.compatible_devices.join('\n')
        setFormData((prev) => ({
          ...prev,
          compatible_products: devicesText,
        }))
      } else if (response.data?.error) {
        console.error('Compatibility search error:', response.data.error)
      }
    } catch (error) {
      console.error('Compatibility search failed:', error)
    } finally {
      setIsSearchingCompatibility(false)
    }
  }

  // Handle creating new category inline
  const handleCreateNewCategory = async () => {
    if (!newCategoryName.trim()) return

    try {
      const newCategory = await createCategory.mutateAsync({
        name: newCategoryName.trim(),
        parent_id: newCategoryParentId,
      })
      if (newCategory.data) {
        await refetchCategories()
        setFormData((prev) => ({
          ...prev,
          category_id: newCategory.data!.id,
        }))
        setShowNewCategory(false)
        setNewCategoryName('')
        setNewCategoryParentId(null)
      }
    } catch (err) {
      console.error('Failed to create category:', err)
    }
  }

  // Handle creating new location inline
  const handleCreateNewLocation = async () => {
    if (!newLocationName.trim()) return

    try {
      const newLocation = await createLocation.mutateAsync({
        name: newLocationName.trim(),
      })
      if (newLocation.data) {
        await refetchLocations()
        setFormData((prev) => ({
          ...prev,
          location_id: newLocation.data!.id,
        }))
        setShowNewLocation(false)
        setNewLocationName('')
      }
    } catch (err) {
      console.error('Failed to create location:', err)
    }
  }

  // Handle creating new manufacturer inline
  const handleCreateNewManufacturer = async () => {
    if (!newManufacturerName.trim()) return

    try {
      const newManufacturer = await createManufacturer.mutateAsync({
        name: newManufacturerName.trim(),
      })
      if (newManufacturer.data) {
        await refetchManufacturers()
        setFormData((prev) => ({
          ...prev,
          manufacturer_id: newManufacturer.data!.id,
        }))
        setShowNewManufacturer(false)
        setNewManufacturerName('')
      }
    } catch (err) {
      console.error('Failed to create manufacturer:', err)
    }
  }

  const [formData, setFormData] = useState<ProductCreate>({
    name: '',
    description: '',
    details: '',
    compatible_products: '',
    sku: '',
    barcode: initialBarcode,
    service_tag: '',
    quantity: 1,
    status: 'in_stock',
    cost_price: '',
    sale_price: '',
    category_id: null,
    location_id: null,
    manufacturer_id: null,
  })

  // Populate form when editing
  useEffect(() => {
    if (product) {
      setFormData({
        name: product.name,
        description: product.description ?? '',
        details: product.details ?? '',
        compatible_products: product.compatible_products ?? '',
        sku: product.sku ?? '',
        barcode: product.barcode ?? '',
        service_tag: product.service_tag ?? '',
        quantity: product.quantity,
        status: product.status ?? 'in_stock',
        cost_price: product.cost_price ?? '',
        sale_price: product.sale_price ?? '',
        category_id: product.category_id,
        location_id: product.location_id,
        manufacturer_id: product.manufacturer_id,
      })
    }
  }, [product])

  // Set default location to "No location yet" for new products
  useEffect(() => {
    if (!isEditing && locationsData?.data && !formData.location_id) {
      const defaultLocation = locationsData.data.find((loc) => loc.name === 'No location yet')
      if (defaultLocation) {
        setFormData((prev) => ({ ...prev, location_id: defaultLocation.id }))
      }
    }
  }, [isEditing, locationsData, formData.location_id])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setSubmitError(null) // Clear any previous error
    if (!formData.name.trim() || !formData.category_id || !formData.location_id) return

    // Clean up empty strings to null
    const cleanedData: ProductCreate = {
      ...formData,
      description: formData.description || null,
      details: formData.details || null,
      compatible_products: formData.compatible_products || null,
      sku: formData.sku || null,
      barcode: formData.barcode || null,
      service_tag: formData.service_tag || null,
      cost_price: formData.cost_price || null,
      sale_price: formData.sale_price || null,
      manufacturer_id: formData.manufacturer_id || null,
    }

    try {
      if (isEditing && product) {
        await updateProduct.mutateAsync({ id: product.id, data: cleanedData })
        navigate(`/products/${product.product_id}`)
      } else {
        const result = await createProduct.mutateAsync(cleanedData)
        if (result.data) {
          // Add pending image if we have one from image search
          if (pendingImageUrl) {
            try {
              await productImageApi.addByUrl(result.data.id, {
                image_url: pendingImageUrl,
                is_primary: true,
                source: 'web_search',
                alt_text: `${result.data.name} - from image search`,
              })
            } catch (imageError) {
              console.error('Failed to add image:', imageError)
              // Continue anyway - product was created
            }
          }
          // Check if we should add another product
          if (saveAndAddAnother) {
            // Reset form for next product but keep location
            const currentLocationId = formData.location_id
            setFormData({
              name: '',
              description: '',
              details: '',
              compatible_products: '',
              sku: '',
              barcode: '',
              service_tag: '',
              quantity: 1,
              status: 'in_stock',
              cost_price: '',
              sale_price: '',
              category_id: null,
              location_id: currentLocationId,
              manufacturer_id: null,
            })
            setPendingImageUrl(null)
            setSaveAndAddAnother(false)
            // Scroll to top for next entry
            window.scrollTo({ top: 0, behavior: 'smooth' })
          } else {
            navigate(`/products/${result.data.product_id}`)
          }
        } else {
          navigate('/products')
        }
      }
    } catch (error) {
      console.error('Failed to save product:', error)
      // Extract error message from response
      if (error instanceof AxiosError && error.response?.data?.detail) {
        setSubmitError(error.response.data.detail)
      } else if (error instanceof Error) {
        setSubmitError(error.message)
      } else {
        setSubmitError('An unexpected error occurred. Please try again.')
      }
      // Scroll to top to show error
      window.scrollTo({ top: 0, behavior: 'smooth' })
    }
  }

  const handleChange = (field: keyof ProductCreate, value: string | number | null) => {
    setFormData((prev) => ({ ...prev, [field]: value }))
  }

  if (isEditing && productLoading) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500 dark:text-gray-400">Loading product...</p>
      </div>
    )
  }

  if (isEditing && !product && !productLoading) {
    return (
      <div className="text-center py-12">
        <p className="text-red-600 dark:text-red-400">Product not found</p>
        <Button variant="secondary" onClick={() => navigate('/products')} className="mt-4">
          Back to Products
        </Button>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            onClick={() =>
              isEditing && product
                ? navigate(`/products/${product.product_id}`)
                : navigate('/products')
            }
          >
            <ArrowLeft size={20} />
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
              {isEditing ? 'Edit Product' : 'New Product'}
            </h1>
            <p className="text-gray-500 dark:text-gray-400">
              {isEditing ? `Editing ${product?.product_id}` : 'Add a new item to inventory'}
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          {!isEditing && (
            <Button
              type="submit"
              form="product-form"
              variant="secondary"
              icon={<Plus size={16} />}
              loading={createProduct.isPending && saveAndAddAnother}
              disabled={!formData.name.trim() || !formData.category_id || !formData.location_id || createProduct.isPending}
              onClick={() => setSaveAndAddAnother(true)}
            >
              Save & Add Another
            </Button>
          )}
          <Button
            type="submit"
            form="product-form"
            icon={<Save size={16} />}
            loading={createProduct.isPending && !saveAndAddAnother || updateProduct.isPending}
            disabled={!formData.name.trim() || !formData.category_id || !formData.location_id}
            onClick={() => setSaveAndAddAnother(false)}
          >
            {isEditing ? 'Update Product' : 'Create Product'}
          </Button>
        </div>
      </div>

      {/* Error banner */}
      {submitError && (
        <div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg p-4 flex items-start gap-3">
          <AlertTriangle className="text-red-500 dark:text-red-400 flex-shrink-0 mt-0.5" size={20} />
          <div className="flex-1">
            <h3 className="text-sm font-medium text-red-800 dark:text-red-200">
              Failed to save product
            </h3>
            <p className="text-sm text-red-700 dark:text-red-300 mt-1">
              {submitError}
            </p>
          </div>
          <button
            type="button"
            onClick={() => setSubmitError(null)}
            className="text-red-500 hover:text-red-700 dark:text-red-400 dark:hover:text-red-200"
          >
            <X size={16} />
          </button>
        </div>
      )}

      {/* Form */}
      <form id="product-form" onSubmit={handleSubmit}>
        <div className="grid gap-6 lg:grid-cols-3">
          {/* Main details */}
          <div className="lg:col-span-2 space-y-6">
            <Card>
              <CardHeader title="Product Information" />
              <CardContent className="space-y-4">
                <Input
                  label="Product Name"
                  value={formData.name}
                  onChange={(e) => handleChange('name', e.target.value)}
                  placeholder="e.g., DDR4 RAM 16GB"
                  required
                />
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Description
                  </label>
                  <textarea
                    value={formData.description ?? ''}
                    onChange={(e) => handleChange('description', e.target.value)}
                    placeholder="Brief product description..."
                    rows={3}
                    className="w-full rounded-lg border border-gray-300 dark:border-gray-600 px-3 py-2 text-sm focus:border-primary-500 focus:ring-1 focus:ring-primary-500 dark:bg-gray-800 dark:text-white dark:placeholder-gray-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Product Details
                  </label>
                  <textarea
                    value={formData.details ?? ''}
                    onChange={(e) => handleChange('details', e.target.value)}
                    placeholder="Extended details, specifications, notes..."
                    rows={5}
                    className="w-full rounded-lg border border-gray-300 dark:border-gray-600 px-3 py-2 text-sm focus:border-primary-500 focus:ring-1 focus:ring-primary-500 dark:bg-gray-800 dark:text-white dark:placeholder-gray-500"
                  />
                </div>
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      Compatible Products
                    </label>
                    <button
                      type="button"
                      onClick={handleCompatibilitySearch}
                      disabled={!formData.name?.trim() || isSearchingCompatibility}
                      className={`flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium transition-colors ${
                        formData.name?.trim() && !isSearchingCompatibility
                          ? 'bg-primary-600 text-white hover:bg-primary-700'
                          : 'bg-gray-300 dark:bg-gray-600 text-gray-500 dark:text-gray-400 cursor-not-allowed'
                      }`}
                      title="Search manufacturer websites for compatible devices"
                    >
                      {isSearchingCompatibility ? (
                        <>
                          <Loader2 size={14} className="animate-spin" />
                          <span>Searching...</span>
                        </>
                      ) : (
                        <>
                          <Sparkles size={14} />
                          <span>AI Search</span>
                        </>
                      )}
                    </button>
                  </div>
                  <textarea
                    value={formData.compatible_products ?? ''}
                    onChange={(e) => handleChange('compatible_products', e.target.value)}
                    placeholder="Compatible laptops, motherboards, servers...&#10;e.g., Dell OptiPlex 7090&#10;HP ProDesk 400 G7&#10;Lenovo ThinkCentre M70s"
                    rows={5}
                    className="w-full rounded-lg border border-gray-300 dark:border-gray-600 px-3 py-2 text-sm focus:border-primary-500 focus:ring-1 focus:ring-primary-500 dark:bg-gray-800 dark:text-white dark:placeholder-gray-500"
                  />
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                    List of compatible devices/systems. Click "AI Search" to auto-populate from manufacturer websites.
                  </p>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      SKU / Part Number
                    </label>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={formData.sku ?? ''}
                        onChange={(e) => handleChange('sku', e.target.value)}
                        placeholder="e.g., KVR32N22S8/16"
                        className="flex-1 min-w-0 rounded-lg border border-gray-300 dark:border-gray-600 px-3 py-2 text-sm focus:border-primary-500 focus:ring-1 focus:ring-primary-500 dark:bg-gray-800 dark:text-white dark:placeholder-gray-500"
                      />
                      <button
                        type="button"
                        onClick={() => setShowSKULookup(true)}
                        disabled={!formData.sku?.trim()}
                        className={`flex-shrink-0 px-3 py-2 rounded-lg transition-colors flex items-center gap-1.5 text-sm font-medium ${
                          formData.sku?.trim()
                            ? 'bg-primary-600 text-white hover:bg-primary-700'
                            : 'bg-gray-300 dark:bg-gray-600 text-gray-500 dark:text-gray-400 cursor-not-allowed'
                        }`}
                        title="Look up product info by SKU"
                      >
                        <Sparkles size={16} />
                        <span className="hidden sm:inline">Lookup</span>
                      </button>
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Barcode (UPC/EAN)
                    </label>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={formData.barcode ?? ''}
                        onChange={(e) => handleChange('barcode', e.target.value)}
                        placeholder="e.g., 740617310894"
                        className="flex-1 min-w-0 rounded-lg border border-gray-300 dark:border-gray-600 px-3 py-2 text-sm focus:border-primary-500 focus:ring-1 focus:ring-primary-500 dark:bg-gray-800 dark:text-white dark:placeholder-gray-500"
                      />
                      <button
                        type="button"
                        onClick={() => setShowBarcodeScanner(true)}
                        className="flex-shrink-0 px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 hover:border-primary-500 hover:text-primary-600 dark:hover:text-primary-400 transition-colors"
                        title="Scan barcode with camera"
                      >
                        <Camera size={16} />
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          if (formData.barcode?.trim()) {
                            handleBarcodeLookup(formData.barcode.trim())
                          }
                        }}
                        disabled={!formData.barcode?.trim() || barcodeLookup.isPending}
                        className={`flex-shrink-0 px-3 py-2 rounded-lg transition-colors flex items-center gap-1.5 text-sm font-medium ${
                          formData.barcode?.trim() && !barcodeLookup.isPending
                            ? 'bg-primary-600 text-white hover:bg-primary-700'
                            : 'bg-gray-300 dark:bg-gray-600 text-gray-500 dark:text-gray-400 cursor-not-allowed'
                        }`}
                        title="Look up product info by barcode"
                      >
                        {barcodeLookup.isPending ? (
                          <Loader2 size={16} className="animate-spin" />
                        ) : (
                          <Search size={16} />
                        )}
                        <span className="hidden sm:inline">Lookup</span>
                      </button>
                    </div>
                    {/* Barcode lookup status */}
                    {(() => {
                      const statusDisplay = getBarcodeLookupStatusDisplay()
                      return statusDisplay ? (
                        <div className={`flex items-center gap-1.5 mt-1 ${statusDisplay.color}`}>
                          {statusDisplay.icon}
                          <span className="text-xs">{statusDisplay.message}</span>
                        </div>
                      ) : null
                    })()}
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Service Tag
                  </label>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={formData.service_tag ?? ''}
                      onChange={(e) => handleChange('service_tag', e.target.value)}
                      placeholder="e.g., Dell/HP service tag or serial number"
                      className="flex-1 min-w-0 rounded-lg border border-gray-300 dark:border-gray-600 px-3 py-2 text-sm focus:border-primary-500 focus:ring-1 focus:ring-primary-500 dark:bg-gray-800 dark:text-white dark:placeholder-gray-500"
                    />
                    <select
                      value={serviceTagManufacturer}
                      onChange={(e) => setServiceTagManufacturer(e.target.value)}
                      className="flex-shrink-0 w-24 rounded-lg border border-gray-300 dark:border-gray-600 px-2 py-2 text-sm focus:border-primary-500 focus:ring-1 focus:ring-primary-500 dark:bg-gray-800 dark:text-white"
                      title="Select manufacturer for lookup"
                    >
                      <option value="auto">Auto</option>
                      <option value="dell">Dell</option>
                      <option value="hp">HP</option>
                      <option value="lenovo">Lenovo</option>
                    </select>
                    <button
                      type="button"
                      onClick={() => setShowServiceTagLookup(true)}
                      disabled={!formData.service_tag?.trim()}
                      className={`flex-shrink-0 px-3 py-2 rounded-lg transition-colors flex items-center gap-1.5 text-sm font-medium ${
                        formData.service_tag?.trim()
                          ? 'bg-primary-600 text-white hover:bg-primary-700'
                          : 'bg-gray-300 dark:bg-gray-600 text-gray-500 dark:text-gray-400 cursor-not-allowed'
                      }`}
                      title="Look up device info by service tag"
                    >
                      <Sparkles size={16} />
                      <span className="hidden sm:inline">Lookup</span>
                    </button>
                  </div>
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                    Enter service tag, select manufacturer (or Auto to detect), and click Lookup
                  </p>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader title="Pricing" />
              <CardContent>
                <div className="grid grid-cols-2 gap-4">
                  <Input
                    label="Cost Price"
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.cost_price ?? ''}
                    onChange={(e) => handleChange('cost_price', e.target.value)}
                    placeholder="0.00"
                  />
                  <Input
                    label="Sale Price"
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.sale_price ?? ''}
                    onChange={(e) => handleChange('sale_price', e.target.value)}
                    placeholder="0.00"
                  />
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* AI Product Identification */}
            <ProductPhotoCapture
              onCapture={handleProductPhotoCapture}
              isAnalyzing={isIdentifying}
            />

            <Card>
              <CardHeader title="Organization" />
              <CardContent className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Category <span className="text-red-500">*</span>
                    {createCategory.isPending && (
                      <span className="ml-2 text-xs text-primary-500">(creating...)</span>
                    )}
                  </label>
                  {showNewCategory ? (
                    <div className="space-y-2">
                      <div className="flex gap-2">
                        <input
                          type="text"
                          value={newCategoryName}
                          onChange={(e) => setNewCategoryName(e.target.value)}
                          placeholder="Enter category name"
                          className="flex-1 rounded-lg border border-gray-300 dark:border-gray-600 px-3 py-2 text-sm focus:border-primary-500 focus:ring-1 focus:ring-primary-500 dark:bg-gray-800 dark:text-white dark:placeholder-gray-500"
                          autoFocus
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault()
                              handleCreateNewCategory()
                            } else if (e.key === 'Escape') {
                              setShowNewCategory(false)
                              setNewCategoryName('')
                              setNewCategoryParentId(null)
                            }
                          }}
                        />
                        <button
                          type="button"
                          onClick={handleCreateNewCategory}
                          disabled={!newCategoryName.trim() || createCategory.isPending}
                          className="px-3 py-2 rounded-lg bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
                          title="Create category"
                        >
                          {createCategory.isPending ? (
                            <Loader2 size={16} className="animate-spin" />
                          ) : (
                            <CheckCircle size={16} />
                          )}
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            setShowNewCategory(false)
                            setNewCategoryName('')
                            setNewCategoryParentId(null)
                          }}
                          className="px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700"
                          title="Cancel"
                        >
                          <X size={16} />
                        </button>
                      </div>
                      <select
                        value={newCategoryParentId ?? ''}
                        onChange={(e) => setNewCategoryParentId(e.target.value ? parseInt(e.target.value) : null)}
                        className="w-full rounded-lg border border-gray-300 dark:border-gray-600 px-3 py-2 text-sm focus:border-primary-500 focus:ring-1 focus:ring-primary-500 dark:bg-gray-800 dark:text-white"
                      >
                        <option value="">No parent (top-level category)</option>
                        {categoriesData?.data?.map((cat) => (
                          <option key={cat.id} value={cat.id}>
                            {cat.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  ) : (
                    <CascadingCategorySelect
                      categories={categoriesData?.data ?? []}
                      value={formData.category_id ?? null}
                      onChange={(categoryId) => handleChange('category_id', categoryId)}
                      onCreateNew={() => setShowNewCategory(true)}
                      required
                    />
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Location <span className="text-red-500">*</span>
                    {createLocation.isPending && (
                      <span className="ml-2 text-xs text-primary-500">(creating...)</span>
                    )}
                  </label>
                  {showNewLocation ? (
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={newLocationName}
                        onChange={(e) => setNewLocationName(e.target.value)}
                        placeholder="Enter location name"
                        className="flex-1 rounded-lg border border-gray-300 dark:border-gray-600 px-3 py-2 text-sm focus:border-primary-500 focus:ring-1 focus:ring-primary-500 dark:bg-gray-800 dark:text-white dark:placeholder-gray-500"
                        autoFocus
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault()
                            handleCreateNewLocation()
                          } else if (e.key === 'Escape') {
                            setShowNewLocation(false)
                            setNewLocationName('')
                          }
                        }}
                      />
                      <button
                        type="button"
                        onClick={handleCreateNewLocation}
                        disabled={!newLocationName.trim() || createLocation.isPending}
                        className="px-3 py-2 rounded-lg bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
                        title="Create location"
                      >
                        {createLocation.isPending ? (
                          <Loader2 size={16} className="animate-spin" />
                        ) : (
                          <CheckCircle size={16} />
                        )}
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setShowNewLocation(false)
                          setNewLocationName('')
                        }}
                        className="px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700"
                        title="Cancel"
                      >
                        <X size={16} />
                      </button>
                    </div>
                  ) : (
                    <div className="flex gap-2">
                      <select
                        value={formData.location_id ?? ''}
                        onChange={(e) =>
                          handleChange('location_id', e.target.value ? parseInt(e.target.value) : null)
                        }
                        required
                        className={`flex-1 rounded-lg border px-3 py-2 text-sm focus:border-primary-500 focus:ring-1 focus:ring-primary-500 dark:bg-gray-800 dark:text-white ${
                          formData.location_id
                            ? 'border-gray-300 dark:border-gray-600'
                            : 'border-red-300 dark:border-red-600'
                        }`}
                      >
                        <option value="">Select a location</option>
                        {locationsData?.data?.map((loc) => (
                          <option key={loc.id} value={loc.id}>
                            {loc.name}
                          </option>
                        ))}
                      </select>
                      <button
                        type="button"
                        onClick={() => setShowNewLocation(true)}
                        className="px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 hover:border-primary-500 hover:text-primary-600 dark:hover:text-primary-400"
                        title="Create new location"
                      >
                        <Plus size={16} />
                      </button>
                    </div>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Manufacturer
                    {createManufacturer.isPending && (
                      <span className="ml-2 text-xs text-primary-500">(creating...)</span>
                    )}
                  </label>
                  {showNewManufacturer ? (
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={newManufacturerName}
                        onChange={(e) => setNewManufacturerName(e.target.value)}
                        placeholder="Enter manufacturer name"
                        className="flex-1 rounded-lg border border-gray-300 dark:border-gray-600 px-3 py-2 text-sm focus:border-primary-500 focus:ring-1 focus:ring-primary-500 dark:bg-gray-800 dark:text-white dark:placeholder-gray-500"
                        autoFocus
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault()
                            handleCreateNewManufacturer()
                          } else if (e.key === 'Escape') {
                            setShowNewManufacturer(false)
                            setNewManufacturerName('')
                          }
                        }}
                      />
                      <button
                        type="button"
                        onClick={handleCreateNewManufacturer}
                        disabled={!newManufacturerName.trim() || createManufacturer.isPending}
                        className="px-3 py-2 rounded-lg bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
                        title="Create manufacturer"
                      >
                        {createManufacturer.isPending ? (
                          <Loader2 size={16} className="animate-spin" />
                        ) : (
                          <CheckCircle size={16} />
                        )}
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setShowNewManufacturer(false)
                          setNewManufacturerName('')
                        }}
                        className="px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700"
                        title="Cancel"
                      >
                        <X size={16} />
                      </button>
                    </div>
                  ) : (
                    <div className="flex gap-2">
                      <select
                        value={formData.manufacturer_id ?? ''}
                        onChange={(e) =>
                          handleChange('manufacturer_id', e.target.value ? parseInt(e.target.value) : null)
                        }
                        className="flex-1 rounded-lg border border-gray-300 dark:border-gray-600 px-3 py-2 text-sm focus:border-primary-500 focus:ring-1 focus:ring-primary-500 dark:bg-gray-800 dark:text-white"
                      >
                        <option value="">No manufacturer</option>
                        {manufacturersData?.data?.map((mfr) => (
                          <option key={mfr.id} value={mfr.id}>
                            {mfr.name}
                          </option>
                        ))}
                      </select>
                      <button
                        type="button"
                        onClick={() => setShowNewManufacturer(true)}
                        className="px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 hover:border-primary-500 hover:text-primary-600 dark:hover:text-primary-400"
                        title="Create new manufacturer"
                      >
                        <Plus size={16} />
                      </button>
                    </div>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Status <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={formData.status ?? 'in_stock'}
                    onChange={(e) => handleChange('status', e.target.value as ProductStatus)}
                    required
                    className="w-full rounded-lg border border-gray-300 dark:border-gray-600 px-3 py-2 text-sm focus:border-primary-500 focus:ring-1 focus:ring-primary-500 dark:bg-gray-800 dark:text-white"
                  >
                    {(Object.keys(PRODUCT_STATUS_LABELS) as ProductStatus[]).map((status) => (
                      <option key={status} value={status}>
                        {PRODUCT_STATUS_LABELS[status]}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Quantity
                  </label>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => handleChange('quantity', Math.max(0, (formData.quantity ?? 0) - 1))}
                      className="p-2.5 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-400 transition-colors"
                    >
                      <Minus size={20} />
                    </button>
                    <input
                      type="number"
                      min="0"
                      max="9999"
                      value={formData.quantity}
                      onChange={(e) => handleChange('quantity', Math.min(9999, parseInt(e.target.value) || 0))}
                      className="w-20 text-center rounded-lg border border-gray-300 dark:border-gray-600 px-2 py-2 text-sm dark:bg-gray-800 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    />
                    <button
                      type="button"
                      onClick={() => handleChange('quantity', Math.min(9999, (formData.quantity ?? 0) + 1))}
                      className="p-2.5 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-400 transition-colors"
                    >
                      <Plus size={20} />
                    </button>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Product Images */}
            <Card>
              <CardHeader title="Product Images" />
              <CardContent className="space-y-4">
                {isEditing && product ? (
                  <>
                    <ImageGallery
                      images={product.images || []}
                      productId={product.id}
                      onImagesChange={handleImageChange}
                      editable
                    />
                    <div className="border-t dark:border-gray-700 pt-4">
                      <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">Add Image</p>
                      <ImageUpload
                        productId={product.id}
                        onImageAdded={handleImageChange}
                      />
                    </div>
                  </>
                ) : pendingImageUrl ? (
                  <div className="text-center py-2">
                    <div className="inline-block border rounded-lg overflow-hidden dark:border-gray-600 mb-2">
                      <img
                        src={pendingImageUrl}
                        alt="Selected product image"
                        className="max-w-[150px] max-h-[150px] object-contain"
                        onError={() => setPendingImageUrl(null)}
                      />
                    </div>
                    <p className="text-sm text-green-600 dark:text-green-400">
                      Image will be saved with product
                    </p>
                    <button
                      type="button"
                      onClick={() => setPendingImageUrl(null)}
                      className="mt-2 text-xs text-red-500 hover:text-red-600"
                    >
                      Remove
                    </button>
                  </div>
                ) : (
                  <div className="text-center py-4 text-gray-500 dark:text-gray-400">
                    <ImageIcon className="mx-auto h-8 w-8 mb-2 opacity-50" />
                    <p className="text-sm">Save product first, or search for images below</p>
                  </div>
                )}

                {/* Search Google Images button */}
                <Button
                  type="button"
                  variant="secondary"
                  className="w-full"
                  icon={<Search size={16} />}
                  onClick={() => setShowImageSearch(true)}
                  disabled={!formData.name.trim()}
                >
                  Search Google Images
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </form>

      {/* Image Search Modal */}
      {showImageSearch && (
        <ImageSearchModal
          query={getImageSearchQuery()}
          onSelect={handleImageSelected}
          onClose={() => setShowImageSearch(false)}
        />
      )}

      {/* SKU Lookup Modal */}
      {showSKULookup && formData.sku && (
        <SKULookupModal
          sku={formData.sku}
          onApply={handleSKULookupApply}
          onClose={() => setShowSKULookup(false)}
        />
      )}

      {/* Barcode Scanner Modal */}
      {showBarcodeScanner && (
        <BarcodeScannerModal
          onScan={handleBarcodeScanned}
          onClose={() => setShowBarcodeScanner(false)}
        />
      )}

      {/* Product Identification Modal */}
      {capturedProductImage && (
        <ProductIdentificationModal
          imageBase64={capturedProductImage}
          onApply={handleProductIdentificationApply}
          onClose={handleIdentificationClose}
        />
      )}

      {/* Service Tag Lookup Modal */}
      {showServiceTagLookup && formData.service_tag && (
        <ServiceTagLookupModal
          serviceTag={formData.service_tag}
          manufacturer={serviceTagManufacturer}
          onApply={handleServiceTagLookupApply}
          onClose={() => setShowServiceTagLookup(false)}
        />
      )}
    </div>
  )
}
